import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Context/AuthContext';
import calendarService from '../../services/calendarService.js';
import './AppointmentScheduler.css';

const AppointmentScheduler = () => {
    const { familyMembers, loading: authLoading } = useAuth();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editingAppointment, setEditingAppointment] = useState(null);
    const [showPastAppointments, setShowPastAppointments] = useState(false);

    // Form state
    const [title, setTitle] = useState('');
    const [type, setType] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [duration, setDuration] = useState(60);
    const [parent, setParent] = useState('');
    const [caregiverId, setCaregiverId] = useState('');
    // Note: mumCaregiverId and dadCaregiverId removed as database doesn't support these columns
    const [notes, setNotes] = useState('');

    const appointmentTypes = [
        { value: 'Medical', label: 'Medical' },
        { value: 'Hair', label: 'Hair' },
        { value: 'Dental', label: 'Dental' },
        { value: 'Optical', label: 'Optical' },
        { value: 'Other', label: 'Other' }
    ];

    const parents = ['Mum', 'Dad', 'Aunty'];

    // API functions
    const fetchAppointments = async () => {
        try {
            setLoading(true);
            console.log('Fetching appointments using calendarService...');

            // Use the calendarService to get all events and filter for appointments
            const allEvents = await calendarService.getAllEvents();
            console.log('All events retrieved:', allEvents);

            // Filter for appointment events
            const appointmentEvents = (allEvents || []).filter(event => {
                // Primary: Check if it's explicitly marked as an appointment
                if (event.isAppointment === true || event.eventType === 'appointment') {
                    return true;
                }
                // Secondary: Check if it has appointment-specific fields
                if (event.appointmentType || event.parent) {
                    return true;
                }
                // Fallback: Check if it has appointment-like description (for legacy data)
                if (event.Description && event.Description.includes('Appointment Type:')) {
                    return true;
                }
                return false;
            });

            console.log('Filtered appointment events:', appointmentEvents);
            console.log(`Found ${appointmentEvents.length} appointments out of ${allEvents.length} total events`);
            setAppointments(appointmentEvents);
            setError(''); // Clear any previous errors
        } catch (err) {
            console.error('Error fetching appointments:', err);
            setError(`Error loading appointments: ${err.message}`);
            setAppointments([]);
        } finally {
            setLoading(false);
        }
    };

    const createAppointment = async (appointmentData) => {
        try {
            console.log('Creating appointment as calendar event...');

            // Create appointment as a calendar event with proper appointment fields
            // Ensure time is handled in local timezone to avoid timeslot issues
            const startDateTime = new Date(`${appointmentData.date}T${appointmentData.time}:00`);
            const endDateTime = new Date(startDateTime.getTime() + (appointmentData.duration * 60000));

            const calendarEvent = {
                Subject: appointmentData.title || 'Untitled Appointment',
                StartTime: startDateTime.toISOString(),
                EndTime: endDateTime.toISOString(),
                IsAllDay: false,
                Description: appointmentData.notes || '',
                FamilyId: parseInt(appointmentData.caregiverId) || 1,
                eventType: 'appointment',
                isAppointment: true,
                isWeekendDuty: false,
                appointmentType: appointmentData.type || 'Other',
                parent: appointmentData.parent || 'Unknown',
                caregiverId: parseInt(appointmentData.caregiverId) || null,
                // Note: Database only has 'caregiverId' column, not separate mum/dad caregiver fields
                // For now, we'll use the main caregiverId field
                // TODO: Consider adding separate mum/dad caregiver columns to database if needed
                duration: appointmentData.duration || 60,
                notes: appointmentData.notes || '',
                eventColor: '#FF6B6B' // Red color for appointments
            };

            console.log('Calendar event payload:', calendarEvent);

            // Use the calendarService to create the event
            const createdEvent = await calendarService.createEvent(calendarEvent);
            console.log('Appointment created successfully:', createdEvent);

            await fetchAppointments(); // Refresh the list
            return true;
        } catch (err) {
            console.error('Error creating appointment:', err);
            setError(`Error creating appointment: ${err.message}`);
            return false;
        }
    };

    const updateAppointment = async (id, appointmentData) => {
        try {
            // Update appointment as a calendar event with proper appointment fields
            // Ensure time is handled in local timezone to avoid timeslot issues
            const startDateTime = new Date(`${appointmentData.date}T${appointmentData.time}:00`);
            const endDateTime = new Date(startDateTime.getTime() + (appointmentData.duration * 60000));

            const calendarEvent = {
                Subject: appointmentData.title || 'Untitled Appointment',
                StartTime: startDateTime.toISOString(),
                EndTime: endDateTime.toISOString(),
                IsAllDay: false,
                Description: appointmentData.notes || '',
                FamilyId: parseInt(appointmentData.caregiverId) || 1,
                eventType: 'appointment',
                isAppointment: true,
                isWeekendDuty: false,
                appointmentType: appointmentData.type || 'Other',
                parent: appointmentData.parent || 'Unknown',
                caregiverId: parseInt(appointmentData.caregiverId) || null,
                // Note: Database only has 'caregiverId' column, not separate mum/dad caregiver fields
                // For now, we'll use the main caregiverId field
                // TODO: Consider adding separate mum/dad caregiver columns to database if needed
                duration: appointmentData.duration || 60,
                notes: appointmentData.notes || '',
                eventColor: '#FF6B6B' // Red color for appointments
            };

            // Use the calendarService to update the event
            const updatedEvent = await calendarService.updateEvent(id, calendarEvent);
            console.log('Appointment updated successfully:', updatedEvent);

            await fetchAppointments(); // Refresh the list
            return true;
        } catch (err) {
            setError(err.message);
            console.error('Error updating appointment:', err);
            return false;
        }
    };

    const deleteAppointment = async (id) => {
        try {
            // Use the calendarService to delete the event
            await calendarService.deleteEvent(id);
            console.log('Appointment deleted successfully:', id);

            await fetchAppointments(); // Refresh the list
            return true;
        } catch (err) {
            setError(err.message);
            console.error('Error deleting appointment:', err);
            return false;
        }
    };

    // Load appointments on component mount
    useEffect(() => {
        fetchAppointments();
    }, []);

    const resetForm = () => {
        setTitle('');
        setType('');
        setDate('');
        setTime('');
        setDuration(60);
        setParent('');
        setCaregiverId('');
        // Note: mumCaregiverId and dadCaregiverId removed as database doesn't have these columns
        setNotes('');
        setEditingAppointment(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title || !date || !time || !parent) {
            setError('Please fill in all required fields');
            return;
        }

        const appointmentData = {
            title,
            type,
            date,
            time,
            duration: parseInt(duration),
            parent,
            caregiverId: caregiverId ? parseInt(caregiverId) : null,
            // Note: mumCaregiverId and dadCaregiverId removed as database doesn't have these columns
            notes
        };

        console.log('handleSubmit - editingAppointment:', editingAppointment);
        console.log('handleSubmit - editingAppointment.id:', editingAppointment?.id);

        let success;
        if (editingAppointment && editingAppointment.id) {
            console.log('Updating appointment with ID:', editingAppointment.id);
            success = await updateAppointment(editingAppointment.id, appointmentData);
        } else {
            console.log('Creating new appointment');
            success = await createAppointment(appointmentData);
        }

        if (success) {
            resetForm();
            setError('');
        }
    };

    const startEditingAppointment = (appointment) => {
        setEditingAppointment(appointment);
        setTitle(appointment.Subject || appointment.title || '');
        setType(appointment.appointmentType || appointment.type || '');
        setDate(appointment.StartTime ? new Date(appointment.StartTime).toISOString().split('T')[0] : appointment.date || '');
        setTime(appointment.StartTime ? new Date(appointment.StartTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : appointment.time || '');
        setDuration(appointment.duration || 60);
        setParent(appointment.parent || '');
        setCaregiverId(appointment.caregiverId || '');
        // Note: mumCaregiverId and dadCaregiverId removed as database doesn't have these columns
        setNotes(appointment.notes || '');
    };

    const cancelEditing = () => {
        resetForm();
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this appointment?')) {
            await deleteAppointment(id);
        }
    };



    const getCaregiverName = (caregiverId) => {
        if (!caregiverId) return 'Unassigned';
        const member = familyMembers.find(m => m.id === caregiverId);
        return member ? member.name : 'Unassigned';
    };

    const getAppointmentDisplayData = (appointment) => {
        // Handle enhanced calendar event format
        return {
            title: appointment.Subject || 'Untitled',
            type: appointment.appointmentType || 'Unknown',
            date: appointment.StartTime ? new Date(appointment.StartTime).toLocaleDateString() : 'Unknown',
            time: appointment.StartTime ? new Date(appointment.StartTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Unknown',
            parent: appointment.parent || 'Unknown',
            caregiver: getCaregiverName(appointment.caregiverId || appointment.FamilyId),
            notes: appointment.notes || appointment.Description || ''
        };
    };


    if (loading || authLoading) {
        return <div className="appointment-scheduler">
            <div className="loading-state">
                <h2>Loading Appointments...</h2>
                <p>Please wait while we fetch your appointment data...</p>
            </div>
        </div>;
    }

    return (
        <div className="appointment-scheduler">
            <div className="appointment-header">
                <h2>🏥 Appointment Scheduler</h2>
                <p>Manage medical appointments, hair appointments, and other scheduled activities for Mum and Dad</p>

                <button
                    onClick={() => setEditingAppointment({})}
                    className="add-appointment-btn"
                    style={{ display: editingAppointment ? 'none' : 'block' }}
                >
                    ➕ Add New Appointment
                </button>

                {editingAppointment && (
                    <button
                        onClick={() => setEditingAppointment(null)}
                        className="cancel-btn"
                        style={{ marginLeft: '1rem' }}
                    >
                        ❌ Cancel
                    </button>
                )}
            </div>

            {error && <div className="error-state">{error}</div>}

            {editingAppointment && editingAppointment.id && (
                <div className="edit-appointment-form">
                    <h3>✏️ Edit Appointment</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="title">Title *</label>
                                <input
                                    type="text"
                                    id="title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="type">Type</label>
                                <select
                                    id="type"
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                    className="form-control"
                                >
                                    <option value="">Select Type</option>
                                    {appointmentTypes.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="date">Date *</label>
                                <input
                                    type="date"
                                    id="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="time">Time *</label>
                                <input
                                    type="time"
                                    id="time"
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="duration">Duration (minutes)</label>
                                <input
                                    type="number"
                                    id="duration"
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    className="form-control"
                                    min="15"
                                    step="15"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="parent">Parent *</label>
                                <select
                                    id="parent"
                                    value={parent}
                                    onChange={(e) => setParent(e.target.value)}
                                    className="form-control"
                                    required
                                >
                                    <option value="">Select Parent</option>
                                    {parents.map(p => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="caregiverId">Caregiver</label>
                                <select
                                    id="caregiverId"
                                    value={caregiverId}
                                    onChange={(e) => setCaregiverId(e.target.value)}
                                    className="form-control"
                                >
                                    <option value="">Select Caregiver</option>
                                    {familyMembers.map(member => (
                                        <option key={member.id} value={member.id}>
                                            {member.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Caregiver Selection for Mum/Dad */}
                        {/* Note: Mum/Dad caregiver fields removed as database doesn't support separate columns
                        {(parent === 'Mum' || parent === 'Dad') && (
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="mumCaregiverId">Who will look after Mum?</label>
                                    <select
                                        id="mumCaregiverId"
                                        value={mumCaregiverId}
                                        onChange={(e) => setMumCaregiverId(e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="">Select Caregiver for Mum</option>
                                        {familyMembers.map(member => (
                                            <option key={member.id} value={member.id}>
                                                {member.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="dadCaregiverId">Who will look after Dad?</label>
                                    <select
                                        id="dadCaregiverId"
                                        value={dadCaregiverId}
                                        onChange={(e) => setDadCaregiverId(e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="">Select Caregiver for Dad</option>
                                        {familyMembers.map(member => (
                                            <option key={member.id} value={member.id}>
                                                {member.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                        */}

                        <div className="form-group">
                            <label htmlFor="notes">Notes</label>
                            <textarea
                                id="notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="form-control"
                                rows="3"
                            />
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="save-btn">
                                💾 Update Appointment
                            </button>
                            <button type="button" onClick={cancelEditing} className="cancel-btn">
                                ❌ Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {editingAppointment && !editingAppointment.id && (
                <div className="add-appointment-form">
                    <h3>📅 Add New Appointment</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="title">Title *</label>
                                <input
                                    type="text"
                                    id="title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="type">Type</label>
                                <select
                                    id="type"
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                >
                                    <option value="">Select Type</option>
                                    {appointmentTypes.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="date">Date *</label>
                                <input
                                    type="date"
                                    id="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="time">Time *</label>
                                <input
                                    type="time"
                                    id="time"
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="duration">Duration (minutes)</label>
                                <input
                                    type="number"
                                    id="duration"
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    min="15"
                                    step="15"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="parent">Parent *</label>
                                <select
                                    id="parent"
                                    value={parent}
                                    onChange={(e) => setParent(e.target.value)}
                                    required
                                >
                                    <option value="">Select Parent</option>
                                    {parents.map(p => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="caregiverId">Caregiver</label>
                                <select
                                    id="caregiverId"
                                    value={caregiverId}
                                    onChange={(e) => setCaregiverId(e.target.value)}
                                >
                                    <option value="">Select Caregiver</option>
                                    {familyMembers.map(member => (
                                        <option key={member.id} value={member.id}>
                                            {member.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Caregiver Selection for Mum/Dad */}
                        {/* Note: Mum/Dad caregiver fields removed as database doesn't support separate columns
                        {(parent === 'Mum' || parent === 'Dad') && (
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="mumCaregiverId">Who will look after Mum?</label>
                                    <select
                                        id="mumCaregiverId"
                                        value={mumCaregiverId}
                                        onChange={(e) => setMumCaregiverId(e.target.value)}
                                    >
                                        <option value="">Select Caregiver for Mum</option>
                                        {familyMembers.map(member => (
                                            <option key={member.id} value={member.id}>
                                                {member.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="dadCaregiverId">Who will look after Dad?</label>
                                    <select
                                        id="dadCaregiverId"
                                        value={dadCaregiverId}
                                        onChange={(e) => setDadCaregiverId(e.target.value)}
                                    >
                                        <option value="">Select Caregiver for Dad</option>
                                        {familyMembers.map(member => (
                                            <option key={member.id} value={member.id}>
                                                {member.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                        */}

                        <div className="form-group">
                            <label htmlFor="notes">Notes</label>
                            <textarea
                                id="notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows="3"
                            />
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="save-btn">
                                ➕ Add Appointment
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="appointment-list">
                {(() => {
                    const isPastAppointment = (apt) => {
                        const aptDate = apt.StartTime
                            ? new Date(apt.StartTime)
                            : new Date((apt.date || '') + 'T' + (apt.time || '00:00'));
                        return aptDate < new Date();
                    };

                    const sortedAppointments = [...appointments].sort((a, b) => {
                        const dateA = a.StartTime ? new Date(a.StartTime) : new Date(a.date + 'T' + a.time);
                        const dateB = b.StartTime ? new Date(b.StartTime) : new Date(b.date + 'T' + b.time);
                        return dateA - dateB;
                    });

                    const pastCount = sortedAppointments.filter(isPastAppointment).length;
                    const displayedAppointments = showPastAppointments
                        ? sortedAppointments
                        : sortedAppointments.filter(apt => !isPastAppointment(apt));

                    return (
                        <>
                            <div className="appointment-list-header">
                                <h3>📋 Appointments ({displayedAppointments.length})</h3>
                                {pastCount > 0 && (
                                    <button
                                        type="button"
                                        className="toggle-past-btn"
                                        onClick={() => setShowPastAppointments(!showPastAppointments)}
                                    >
                                        {showPastAppointments
                                            ? '🙈 Hide Past Appointments'
                                            : `👁️ Show Past Appointments (${pastCount})`}
                                    </button>
                                )}
                            </div>

                            {displayedAppointments.length === 0 ? (
                                <div className="no-appointments">
                                    <h4>{appointments.length > 0 ? 'No upcoming appointments' : 'No appointments scheduled'}</h4>
                                    <p>
                                        {appointments.length > 0 && pastCount > 0
                                            ? `You have ${pastCount} past appointment(s). Click "Show Past Appointments" above to view them.`
                                            : 'Click "Add New Appointment" to get started!'}
                                    </p>
                                </div>
                            ) : (
                                displayedAppointments.map(appointment => {
                                    const displayData = getAppointmentDisplayData(appointment);
                                    const isPast = isPastAppointment(appointment);

                                    return (
                                        <div key={appointment.id} className={`appointment-card ${isPast ? 'past' : ''}`}>
                                            <div className="appointment-status"></div>

                                            <div className="appointment-header">
                                                <div className="appointment-title-section">
                                                    <span className="parent-indicator">
                                                        {displayData.parent === 'Mum' ? '👩' :
                                                            displayData.parent === 'Dad' ? '👨' : '👩‍🦱'}
                                                    </span>
                                                    <h4 className="appointment-title">{displayData.title}</h4>
                                                    <span className="appointment-type">{displayData.type}</span>
                                                </div>

                                                <div className="appointment-actions">
                                                    <button
                                                        onClick={() => startEditingAppointment(appointment)}
                                                        className="edit-btn"
                                                        title="Edit Appointment"
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(appointment.id)}
                                                        className="delete-btn"
                                                        title="Delete Appointment"
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="appointment-details">
                                                <div className="appointment-time">
                                                    <strong>Date & Time:</strong> {displayData.date} at {displayData.time}
                                                    {appointment.duration && ` (${appointment.duration} min)`}
                                                </div>

                                                <div className="appointment-parent">
                                                    <strong>Parent:</strong> {displayData.parent}
                                                </div>

                                                {appointment.caregiverId && (
                                                    <div className="appointment-caregiver">
                                                        <strong>Caregiver:</strong> {displayData.caregiver}
                                                    </div>
                                                )}

                                                {displayData.notes && (
                                                    <div className="appointment-notes">
                                                        <strong>Notes:</strong> {displayData.notes}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </>
                    );
                })()}
            </div>
        </div>
    );
};

export default AppointmentScheduler;
